import React, { useState, useMemo } from 'react';

import styled from 'styled-components';
import { AxiosError } from 'axios';

import CSVImportService from '../../../../../services/CSVImportService';
import BulkUploadButton from '../../../../utils/BulkUploadButton';
import Error from '../../../../stateMessage/Error';
import { Activity, BookingOption } from '../../../../../types/Activity';

import ActivitiesPreview from './ActivitiesPreview';
import BookingOptionsPreview from './BookingOptionsPreview';

const StyledWrapper = styled.div`
  height: 100%;
  padding-bottom: 65px;
  margin-bottom: -40px;
  width: 100%;

  h3 {
    margin-bottom: 20px;
  }

  .table-responsive {
    margin-bottom: 20px;
    overflow: auto;
    height: 84%;
    display: block;
    background: #fff;
    border: 1px solid #d9d9d9;

    thead {
      position: sticky;
      top: 0;
      z-index: 1000;
    }

    td {
      border-right: 1px solid #ddd;
    }

    th {
      min-width: 150px;
    }

    th.table-cell__lg {
      min-width: 250px;
    }

    th.table-cell__xlg {
      min-width: 330px;
    }
  }

  p {
    margin-top: 20px;
  }
`;

const StyledHeader = styled.div`
  display: flex;
  justify-content: space-between;
`;

const StyledBulkButtons = styled.div`
  display: flex;

  > div {
    margin-right: 20px;
  }
`;

const StyledTabs = styled.ul`
  display: flex;
  margin: 0;
  li {
    list-style-type: none;
    margin-right: 10px;
    color: #43a5c6;
    cursor: pointer;
  }

  button {
    background: inherit;
    outline: none;
    border: none;
    padding: 6px 30px 5px 30px;
    margin: 0;
  }

  .active {
    background: #ffffff;
    color: #000000;
    border: 1px solid #d9d9d9;
    border-top-left-radius: 4px;
    border-top-right-radius: 4px;
    border-bottom: 0;
  }
`;

const DISPLAY_OPTIONS = {
  activities: 'activities',
  bookingOptions: 'bookingOptions',
};

type Props = {
  activities: Activity[];
  bookingOptions: BookingOption[];
  setActivities: (activities: Activity[]) => void;
  setBookingOptions: (bookingOptions: BookingOption[]) => void;
};

const ActivitiesBulkUploadPreviewContent = ({
  activities,
  bookingOptions,
  setActivities,
  setBookingOptions,
}: Props) => {
  const [displayedTab, setDisplayedTab] = useState(DISPLAY_OPTIONS.activities);

  const [error, setError] = useState<string | undefined>(undefined);

  const onBulkUploadChange = async (event, type) => {
    try {
      const data = await CSVImportService.readFile(event);
      if (type === DISPLAY_OPTIONS.activities) {
        setActivities(data as Activity[]);
        setDisplayedTab(DISPLAY_OPTIONS.activities);
      } else {
        setBookingOptions(data as BookingOption[]);
        setDisplayedTab(DISPLAY_OPTIONS.bookingOptions);
      }
    } catch (e) {
      const error = e as AxiosError;
      setError(
        `Error code: ${error?.response?.status} \nError message: ${error?.response?.data}`
      );
    }
  };

  const renderDisplayTab = useMemo(() => {
    switch (displayedTab) {
      case DISPLAY_OPTIONS.activities:
        return <ActivitiesPreview activities={activities} />;
      case DISPLAY_OPTIONS.bookingOptions:
        return (
          <BookingOptionsPreview
            activities={activities}
            bookingOptions={bookingOptions}
          />
        );
      default:
        return null;
    }
  }, [activities, bookingOptions, displayedTab]);

  return (
    <StyledWrapper>
      {!!error && <Error text={error} />}
      <StyledHeader>
        <h3>Bulk Upload</h3>
        <StyledBulkButtons>
          <BulkUploadButton
            onChange={event =>
              onBulkUploadChange(event, DISPLAY_OPTIONS.activities)
            }
            label='Products Upload'
          />
          <BulkUploadButton
            onChange={event =>
              onBulkUploadChange(event, DISPLAY_OPTIONS.bookingOptions)
            }
            label='Booking Options Upload'
          />
        </StyledBulkButtons>
      </StyledHeader>
      <StyledTabs>
        <li>
          <button
            type='button'
            onClick={() => setDisplayedTab(DISPLAY_OPTIONS.activities)}
            className={`${
              displayedTab === DISPLAY_OPTIONS.activities ? 'active' : ''
            }`}
          >
            Experiences
          </button>
        </li>
        <li>
          <button
            type='button'
            onClick={() => setDisplayedTab(DISPLAY_OPTIONS.bookingOptions)}
            className={`${
              displayedTab === DISPLAY_OPTIONS.bookingOptions ? 'active' : ''
            }`}
          >
            Booking Options
          </button>
        </li>
      </StyledTabs>
      {renderDisplayTab}
    </StyledWrapper>
  );
};

export default ActivitiesBulkUploadPreviewContent;
